import type { ToneNotation } from "@kensio/pinyinjs";

import { element } from "./dom.js";
import type { SegmentedWord } from "./segmented-words.js";
import { markPinyinWord } from "./syllable-text.js";
import { toneClass } from "./tone-colour.js";
import { frequencyBandOf } from "./word-frequency.js";

/**
 * Drawing the word list.
 *
 * Apart from the tool for the reason `output.ts` is apart from the hanzi tool.
 * What a row looks like changes for editorial reasons and what the tool does
 * changes for behavioural ones. What a reader pastes is built in
 * `segmenter-copy.ts`, out of the same words and the same bands.
 */

/**
 * The word's pinyin, one coloured span per syllable.
 *
 * Per syllable rather than as one string, because the site colours pinyin by
 * tone and has since long before these tools existed. `markPinyinWord` puts
 * the 隔音符号 on the boundary that takes it, which is why the apostrophe
 * cannot be added by writing each syllable alone.
 *
 * The reading is the one the decoder settled on for this occurrence, so 得
 * after a verb says `de` here whatever 得 says elsewhere in the same text.
 */
function pinyinNodes(
  word: SegmentedWord,
  notation: ToneNotation,
): readonly Node[] {
  return markPinyinWord(word.reading, notation).map((text, at) => {
    const tone = toneClass(word.reading[at]?.tone);

    return tone === undefined
      ? document.createTextNode(text)
      : element("span", { text, className: tone });
  });
}

/**
 * One word as a table row.
 *
 * `lang="zh"` on the characters rather than `zh-Hans` or `zh-Hant`. Which
 * script the text is in would take the conversion tables, which is 100 KB this
 * tool fetches for nothing else, and a page that guessed would put the wrong
 * font on half of what a reader pasted. `zh` is true of both.
 */
export function wordRow(
  word: SegmentedWord,
  notation: ToneNotation,
): HTMLTableRowElement {
  const band = frequencyBandOf(word.frequency);

  return element("tr", {
    className: "cb-segmenter-row",
    children: [
      element("th", {
        className: "cb-segmenter-hanzi hanzi",
        attributes: { scope: "row", lang: "zh" },
        text: word.text,
      }),
      element("td", {
        className: "cb-segmenter-pinyin pinyin",
        attributes: { lang: "zh-Latn-CN-pinyin" },
        children: pinyinNodes(word, notation),
      }),
      element("td", {
        className: "cb-segmenter-count",
        text: String(word.count),
      }),
      element("td", {
        className: "cb-segmenter-band",
        text: band?.name ?? "",
      }),
    ],
  });
}

/** The whole list as rows, ready to go into the table body. */
export function wordRows(
  words: readonly SegmentedWord[],
  notation: ToneNotation,
): readonly Node[] {
  return words.map((word) => wordRow(word, notation));
}
